import { useState } from "react";
import { Loader2, MoveRight, MoveLeft, ArrowUpFromLine, ArrowDownToLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { moveCommands } from "../../lib/transport";

const STEP_IN = 4;

// Three tiny pen-up moves, each followed by a question. Answers are saved into the calibration so every cut file is
// generated with the right sign along the feed (feed_dir), across the roll (cross_dir) and the plotter's origin behaviour.
export const DirectionTest = ({ cal, fmt, send, busy, onSave }) => {
  const [step, setStep] = useState(0);
  const [moved, setMoved] = useState(false);
  const [ans, setAns] = useState({ feed_dir: cal.feed_dir || 1, cross_dir: cal.cross_dir || -1, origin_mode: cal.origin_mode || "auto" });
  const go = async (cmds, label) => { await send(cmds, label); setMoved(true); };
  const pick = (patch) => { setAns((a) => ({ ...a, ...patch })); setMoved(false); setStep((s) => s + 1); };
  const fd = ans.feed_dir;
  const steps = [
    {
      title: `1 · Film feed direction`,
      text: `Press Move: the plotter moves the film ${STEP_IN}" with the blade up. Watch which way the film goes.`,
      move: () => go(moveCommands(fmt, STEP_IN, 0, cal), `${STEP_IN}" feed test`),
      question: "Was that the direction the film should feed at the START of a cut?",
      options: [
        { id: "feed-ok", icon: <CheckCircle2 className="h-4 w-4" />, label: "Yes — that's forward", on: () => pick({ feed_dir: 1 }) },
        { id: "feed-reverse", icon: <ArrowDownToLine className="h-4 w-4" />, label: "No — it went in reverse", on: () => pick({ feed_dir: -1 }) },
      ],
    },
    {
      title: "2 · Origin between jobs",
      text: `Press Move: the plotter is told to go to its origin (0,0) as a new job.`,
      move: () => go(moveCommands(fmt, 0, 0, cal, true), "Origin test"),
      question: "Did the film move back to where step 1 started?",
      options: [
        { id: "origin-auto", icon: <CheckCircle2 className="h-4 w-4" />, label: "No — nothing moved (each job starts where the blade is)", on: () => pick({ origin_mode: "auto" }) },
        { id: "origin-absolute", icon: <ArrowUpFromLine className="h-4 w-4" />, label: "Yes — it moved back (plotter keeps its origin)", on: () => pick({ origin_mode: "absolute" }) },
      ],
    },
    {
      title: "3 · Across the film",
      text: `Press Move: the blade slides ${STEP_IN}" across the film (no film movement).`,
      move: () => go(moveCommands(fmt, 0, STEP_IN, cal), `${STEP_IN}" across test`),
      question: "Facing the plotter, which way did the blade move?",
      options: [
        { id: "cross-left", icon: <MoveLeft className="h-4 w-4" />, label: "Left — I'll set the origin at the film's RIGHT edge", on: () => pick({ cross_dir: -1 }) },
        { id: "cross-right", icon: <MoveRight className="h-4 w-4" />, label: "Right — I'll set the origin at the film's LEFT edge", on: () => pick({ cross_dir: 1 }) },
      ],
    },
  ];
  if (step >= steps.length) {
    return (
      <div className="space-y-3" data-testid="direction-result">
        <div className="rounded-md border border-[#3ecf8e]/40 bg-[#3ecf8e]/10 px-3 py-3 text-[13px] text-[#3ecf8e]">Direction test complete.</div>
        <ul className="space-y-1 rounded-md border border-[#27324a] bg-[#0c1017] px-3 py-2 text-[12px] text-[#c7cbd1]">
          <li data-testid="result-feed">Feed: {fd < 0 ? "flipped — cut files use negative X so the film feeds your way" : "standard (+X)"}</li>
          <li data-testid="result-origin">Origin: {ans.origin_mode === "absolute" ? "plotter keeps its origin — press Offline, jog to fresh film, then Online/Origin before every cut" : "auto — every cut starts where the blade is parked"}</li>
          <li data-testid="result-cross">Across: origin at the film's {ans.cross_dir > 0 ? "LEFT" : "RIGHT"} edge; the pattern extends {ans.cross_dir > 0 ? "right" : "left"} by its own width</li>
        </ul>
        <div className="flex gap-2">
          <button data-testid="direction-redo-btn" className="btn" onClick={() => { setStep(0); setMoved(false); }}>Run again</button>
          <button data-testid="direction-save-btn" className="btn-red flex-1" onClick={() => { onSave(ans); toast.success("Plotter directions saved"); }}>Save</button>
        </div>
      </div>
    );
  }
  const s = steps[step];
  return (
    <div className="space-y-3" data-testid={`direction-step-${step + 1}`}>
      <div className="text-[13px] text-[#e8e9ec]">{s.title}</div>
      <p className="text-[12px] text-[#9aa0a8]">{s.text}</p>
      <button data-testid="direction-move-btn" className="btn-red w-full" disabled={busy} onClick={s.move}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoveRight className="h-4 w-4" />} Move</button>
      {moved && (
        <div className="space-y-2 rounded-md border border-[#27324a] bg-[#0c1017] p-3">
          <div className="text-[12px] text-[#e8e9ec]">{s.question}</div>
          {s.options.map((o) => <button key={o.id} data-testid={`direction-${o.id}`} className="btn w-full justify-start" onClick={o.on}>{o.icon} {o.label}</button>)}
        </div>
      )}
      <div className="text-[11px] text-[#6b7079]">Step {step + 1} of {steps.length} · current: feed {cal.feed_dir < 0 ? "flipped" : "standard"} · origin {cal.origin_mode || "auto"} · across from the {cal.cross_dir > 0 ? "left" : "right"}</div>
    </div>
  );
};
